import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, UploadSimple, FileCsv, CheckCircle, Warning } from "@phosphor-icons/react";

/**
 * Bulk-invite modal launched from the Nudge Queue header.
 * Accepts a CSV of email/name/phone plus a target job_id, POSTs to
 * /api/candidates/bulk-invite, and shows a per-row summary.
 */
export default function BulkInviteModal({ open, onClose, onDone }) {
    const [file, setFile] = useState(null);
    const [jobs, setJobs] = useState([]);
    const [jobId, setJobId] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        if (!open) return;
        setFile(null);
        setResult(null);
        api.get("/jobs")
            .then((r) => {
                const items = Array.isArray(r.data) ? r.data : r.data?.items || [];
                setJobs(items.filter((j) => j.status !== "archived"));
            })
            .catch(() => {});
    }, [open]);

    if (!open) return null;

    const submit = async (e) => {
        e.preventDefault();
        if (!file) {
            toast.error("Please choose a CSV file.");
            return;
        }
        if (!jobId) {
            toast.error("Please choose the target requirement.");
            return;
        }
        setBusy(true);
        setResult(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("job_id", jobId);
            const { data } = await api.post("/candidates/bulk-invite", fd);
            setResult(data);
            toast.success(`Invited ${data.created} · Skipped ${data.skipped_duplicates}`);
            onDone?.(data);
        } catch (ex) {
            toast.error(ex?.response?.data?.detail || "Bulk invite failed.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="bulk-invite-modal"
            className="fixed inset-0 z-[70] bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
        >
            <div
                className="bg-white w-full max-w-lg max-h-[92vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between px-5 py-4 border-b border-[#E5E5E5]">
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-cobalt mb-1">
                            Bulk-invite
                        </p>
                        <h3 className="font-heading text-xl font-bold tracking-tight text-ink">
                            Upload a CSV of candidates
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="bulk-invite-close"
                        className="text-[#525252] hover:text-ink"
                        aria-label="Close"
                    >
                        <X size={18} weight="bold" />
                    </button>
                </div>

                <form onSubmit={submit} className="p-5 space-y-4">
                    <div>
                        <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] block mb-1">
                            Requirement
                        </label>
                        <select
                            data-testid="bulk-invite-job"
                            value={jobId}
                            onChange={(e) => setJobId(e.target.value)}
                            className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm focus:outline-none focus:border-cobalt bg-white"
                        >
                            <option value="">Choose a job…</option>
                            {jobs.map((j) => (
                                <option key={j.id} value={j.id}>
                                    {j.title}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label
                            htmlFor="bulk-csv"
                            data-testid="bulk-invite-file-label"
                            className="border-2 border-dashed border-[#E5E5E5] hover:border-cobalt p-6 flex flex-col items-center justify-center text-center cursor-pointer transition-colors"
                        >
                            <UploadSimple size={22} weight="bold" className="text-[#525252] mb-2" />
                            {file ? (
                                <p className="text-sm font-bold text-ink">{file.name}</p>
                            ) : (
                                <p className="text-sm font-bold text-ink">Drop your CSV or click to browse</p>
                            )}
                            <p className="text-[10px] text-[#A3A3A3] mt-1">
                                Columns: <span className="font-mono">email</span>, name (optional), phone (optional)
                            </p>
                            <input
                                id="bulk-csv"
                                type="file"
                                accept=".csv"
                                data-testid="bulk-invite-file"
                                onChange={(e) => setFile(e.target.files?.[0] || null)}
                                className="hidden"
                            />
                        </label>
                    </div>

                    {result && (
                        <div
                            data-testid="bulk-invite-result"
                            className="border border-[#E5E5E5] p-3 space-y-1.5"
                        >
                            <div className="flex items-center gap-2 text-xs">
                                <CheckCircle size={14} weight="bold" className="text-[#059669]" />
                                <span className="font-bold text-ink" data-testid="bulk-invite-created">
                                    {result.created}
                                </span>
                                <span className="text-[#525252]">candidates invited (drip started)</span>
                            </div>
                            {result.skipped_duplicates > 0 && (
                                <div className="flex items-center gap-2 text-xs text-[#525252]">
                                    <span className="font-bold text-ink">{result.skipped_duplicates}</span>
                                    <span>skipped (already on this job)</span>
                                </div>
                            )}
                            {result.errors?.length > 0 && (
                                <div className="mt-2">
                                    <div className="flex items-center gap-2 text-xs">
                                        <Warning size={14} weight="bold" className="text-[#F59E0B]" />
                                        <span className="font-bold text-ink">{result.errors.length}</span>
                                        <span className="text-[#525252]">errors</span>
                                    </div>
                                    <ul className="mt-1 space-y-0.5 text-[10px] text-[#DC2626] font-mono max-h-24 overflow-y-auto">
                                        {result.errors.slice(0, 10).map((e, i) => (
                                            <li key={i}>Row {e.row}: {e.error}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#F5F5F5]">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={busy}
                            className="text-xs font-black uppercase tracking-wider text-[#525252] hover:text-ink px-3 py-2"
                        >
                            {result ? "Close" : "Cancel"}
                        </button>
                        <button
                            type="submit"
                            data-testid="bulk-invite-submit"
                            disabled={busy || !file || !jobId}
                            className="btn-gradient px-4 py-2 text-[11px] font-black uppercase tracking-wider inline-flex items-center gap-1.5 disabled:opacity-50"
                        >
                            <FileCsv size={11} weight="bold" />
                            {busy ? "Uploading…" : "Invite candidates"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
